import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DIAMOND_PACKAGES } from '../config/licenseConfig';
import { supabase } from '../lib/supabase';

interface LicenseKeyRechargeProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (diamonds: number) => void;
  userId?: string;
}

const RechargeOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const RechargePanel = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const RechargeTitle = styled.h2`
  text-align: center;
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
`;

const RechargeCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #000;
  }
`;

const RechargeHeader = styled.div`
  position: relative;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  margin-bottom: 15px;
`;

const LicenseSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 15px;
  color: #333;
  font-size: 16px;
`;

const LicenseInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
`;

const LicenseInput = styled.input`
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const RedeemButton = styled.button<{ $disabled: boolean }>`
  background-color: ${props => props.$disabled ? '#ccc' : '#4caf50'};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 16px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.$disabled ? '#ccc' : '#388e3c'};
  }
`;

const PurchaseSection = styled.div`
  border-top: 1px solid #eee;
  padding-top: 20px;
`;

const PackageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Package = styled.div<{ $isEnglish: boolean }>`
  display: flex;
  align-items: ${props => props.$isEnglish ? 'flex-start' : 'center'};
  justify-content: space-between;
  padding: 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background-color: white;
  transition: all 0.2s;
  flex-direction: ${props => props.$isEnglish ? 'column' : 'row'};
  gap: ${props => props.$isEnglish ? '12px' : '0'};
  
  &:hover {
    border-color: #4caf50;
    background-color: #f7f7f7;
  }
`;

const PackageInfo = styled.div<{ $isEnglish: boolean }>`
  flex: 1;
  ${props => props.$isEnglish ? 'width: 100%;' : ''}
`;

const PackageName = styled.div`
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PackageDescription = styled.div`
  color: #666;
  font-size: 14px;
`;

const PackagePriceAndButton = styled.div<{ $isEnglish: boolean }>`
  display: flex;
  align-items: center;
  gap: 15px;
  ${props => props.$isEnglish ? 'width: 100%; justify-content: space-between;' : ''}
`;

const PackagePrice = styled.div`
  font-weight: bold;
  font-size: 18px;
  color: #e91e63;
`;

const PurchaseButton = styled.button`
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
  
  &:hover {
    background-color: #1976d2;
  }
`;

const DiamondIcon = styled.span`
  font-size: 20px;
`;

const MessageContainer = styled.div`
  margin-top: 15px;
`;

const SuccessMessage = styled.div`
  color: #4caf50;
  padding: 10px;
  background-color: #f0f9f0;
  border-radius: 4px;
  border-left: 4px solid #4caf50;
`;

const ErrorMessage = styled.div`
  color: #f44336;
  padding: 10px;
  background-color: #feeeee;
  border-radius: 4px;
  border-left: 4px solid #f44336;
`;

const LicenseKeyRecharge: React.FC<LicenseKeyRechargeProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  userId 
}) => {
  const { t, i18n } = useTranslation();
  const [licenseKey, setLicenseKey] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // 检测当前语言是否为英语
  const isEnglish = i18n.language === 'en';
  
  if (!isOpen) return null;
  // TODO 测试使用
  // const handleRedeemLicense3 = async () => {
  //   let gumroadData: any = null;
  //   let validProductId: string | null = null;
  //   try {
  //     const gumroadResponse = await fetch('https://api.gumroad.com/v2/licenses/verify', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({
  //         'license_key': 'xxxxx',
  //         // 'increment_uses_count': 'false',
  //         product_id: 'xxxx'
  //       })
  //     });

  //     const responseData = await gumroadResponse.json();
  //     console.log('🔍 Gumroad response:', responseData);
  //     if (responseData.success) {
  //       gumroadData = responseData;
  //       validProductId = responseData?.purchase?.product_id;
  //       console.log('🔍 Gumroad response:', responseData);
  //       if (responseData.success) {
  //         gumroadData = responseData;
  //         validProductId = responseData?.purchase?.product_id;
  //       }
  //     }
  //   } catch (error) {
  //     console.error(`Error verifying license for product ${licenseKey}:`, error);
  //   }
  // }
  
  const handleRedeemLicense = async () => {
    if (!licenseKey.trim() || isProcessing) return;
    
    if (!userId) {
      setMessage({ 
        type: 'error', 
        text: t('licenseRecharge.userRequired')
      });
      return;
    }
    
    try {
      setIsProcessing(true);
      setMessage(null);
      
      // 获取当前用户会话
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      
      if (!session || !session.user) {
        setMessage({ 
          type: 'error', 
          text: t('licenseRecharge.userNotLoggedIn')
        });
        setIsProcessing(false);
        return;
      }
      
      const token = session.access_token;
      
      const response = await fetch('/api/redeem-license', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          licenseKey: licenseKey.trim(),
          userId: userId
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: t('licenseRecharge.redeemSuccess', { diamonds: result.diamonds })
        });
        
        // 调用成功回调
        onSuccess(result.diamonds);
        
        // 清空输入框
        setLicenseKey('');
        
        // 3秒后关闭弹窗
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        setMessage({ 
          type: 'error', 
          text: result.message || t('licenseRecharge.redeemError')
        });
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.message || t('licenseRecharge.networkError')
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handlePurchaseClick = (packageLink: string) => {
    // 在新窗口打开购买链接
    window.open(packageLink, '_blank');
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRedeemLicense();
    }
  };
  
  return (
    <RechargeOverlay>
      <RechargePanel>
        <RechargeHeader>
          <RechargeTitle>{t('licenseRecharge.title')}</RechargeTitle>
          <RechargeCloseButton onClick={onClose}>✕</RechargeCloseButton>
        </RechargeHeader>
        
        {/* 兑换码输入区域 */}
        <LicenseSection>
          <SectionTitle>{t('licenseRecharge.redeemSection')}</SectionTitle>
          <LicenseInputContainer>
            <LicenseInput
              type="text"
              placeholder={t('licenseRecharge.inputPlaceholder')}
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isProcessing}
            />
            <RedeemButton 
              onClick={handleRedeemLicense}
              $disabled={!licenseKey.trim() || isProcessing}
              disabled={!licenseKey.trim() || isProcessing}
            >
              {isProcessing ? t('licenseRecharge.processing') : t('licenseRecharge.redeem')}
            </RedeemButton>
          </LicenseInputContainer>
        </LicenseSection>
        
        {/* 购买区域 */}
        <PurchaseSection>
          <SectionTitle>{t('licenseRecharge.purchaseSection')}</SectionTitle>
          <PackageContainer>
            {DIAMOND_PACKAGES.map((pkg) => (
              <Package key={pkg.id} $isEnglish={isEnglish}>
                <PackageInfo $isEnglish={isEnglish}>
                  <PackageName>
                    <DiamondIcon>💎</DiamondIcon>
                    {t('licenseRecharge.diamondPack', { count: pkg.diamonds })}
                  </PackageName>
                  <PackageDescription>
                    {t('licenseRecharge.packageDescription', { diamonds: pkg.diamonds })}
                  </PackageDescription>
                </PackageInfo>
                <PackagePriceAndButton $isEnglish={isEnglish}>
                  <PackagePrice>{pkg.price}</PackagePrice>
                  <PurchaseButton onClick={() => handlePurchaseClick(pkg.link)}>
                    {t('licenseRecharge.purchase')}
                  </PurchaseButton>
                </PackagePriceAndButton>
              </Package>
            ))}
          </PackageContainer>
        </PurchaseSection>
        
        {/* 消息显示区域 */}
        {message && (
          <MessageContainer>
            {message.type === 'success' ? (
              <SuccessMessage>{message.text}</SuccessMessage>
            ) : (
              <ErrorMessage>{message.text}</ErrorMessage>
            )}
          </MessageContainer>
        )}
      </RechargePanel>
    </RechargeOverlay>
  );
};

export default LicenseKeyRecharge; 